import { Scene, Cursor, Tile } from "views/components/Guide/art/common";
import shared from "views/components/Guide/art/scenes.module.scss";
import classNames from "classnames";

import styles from "./style.module.scss";

const SPREAD_TILES = [
  { x: 20, y: 26, seed: "success" },
  { x: 72, y: 26 },
  { x: 124, y: 26 },
  { x: 176, y: 26, seed: "primary" },
  { x: 20, y: 78 },
  { x: 72, y: 78 },
  { x: 124, y: 78 },
  { x: 176, y: 78 },
  { x: 20, y: 130 },
  { x: 72, y: 130 },
  { x: 124, y: 130 },
  { x: 176, y: 130 },
] as const;

const SPREAD_TONES = [
  "success",
  "success",
  "primary",
  "primary",
  "success",
  "primary",
  "success",
  "primary",
  "primary",
  "success",
  "primary",
  "success",
];

// A few seeds tint the whole grid, one tile at a time.
export const PropagateScene = () => (
  <Scene
    label="Two seed images spread their labels across a grid of images"
    className={styles.seedScene}
  >
    {SPREAD_TILES.map((tile, k) => (
      <g key={k}>
        <Tile x={tile.x} y={tile.y} size={44} />
        {"seed" in tile ? (
          <rect
            className={`ga-seed-ring ga-stroke-${tile.seed}`}
            x={tile.x + 1.5}
            y={tile.y + 1.5}
            width="41"
            height="41"
            rx="6"
          />
        ) : (
          <rect
            className={`ga-spread ga-fill-${SPREAD_TONES[k]}`}
            x={tile.x}
            y={tile.y}
            width="44"
            height="44"
            rx="7"
            style={{ animationDelay: `${0.3 + k * 0.22}s` }}
          />
        )}
      </g>
    ))}
  </Scene>
);

const CHOOSE_ROWS = [
  { y: 66, label: "Scratch" },
  { y: 98, label: "Void" },
  { y: 130, label: "Contamination" },
];

// The Add Labels dialog: two labels get checked, then Add is pressed.
export const ChooseLabelsScene = () => (
  <Scene
    label="Selecting two labels in the Add Labels dialog"
    className={styles.seedScene}
  >
    <rect
      className="ga-surface"
      x="20"
      y="28"
      width="200"
      height="150"
      rx="12"
    />
    <text className="ga-text" x="34" y="50" fontSize="11">
      Add Labels
    </text>
    {CHOOSE_ROWS.map((row, i) => (
      <g key={row.label}>
        <rect
          className="ga-outline ga-surface-deep"
          x="36"
          y={row.y}
          width="13"
          height="13"
          rx="3"
        />
        <path
          className={`ga-check-draw ga-choose-check-${i + 1}`}
          d={`M39 ${row.y + 7} l3 3 l5 -6`}
        />
        <text className="ga-text" x="58" y={row.y + 10} fontSize="10">
          {row.label}
        </text>
      </g>
    ))}
    <rect className="ga-btn" x="96" y="150" width="48" height="20" rx="6" />
    <rect
      className="ga-fill-primary ga-choose-add"
      x="96"
      y="150"
      width="48"
      height="20"
      rx="6"
    />
    <text className="ga-text" x="120" y="163" fontSize="10" textAnchor="middle">
      Add
    </text>
    <Cursor className="ga-cur-choose" />
  </Scene>
);

// Drag a tile onto a label card; the counter ticks from 4 to 5 of 5.
export const DragSeedScene = () => (
  <Scene
    label="Dragging an image onto the Scratch label card"
    className={styles.seedScene}
  >
    <rect
      className="ga-surface"
      x="120"
      y="44"
      width="100"
      height="128"
      rx="12"
    />
    <text className="ga-text" x="130" y="62" fontSize="11">
      Scratch
    </text>
    <rect className="ga-bar" x="130" y="70" width="80" height="4" rx="2" />
    <rect
      className="ga-fill-success ga-drag-progress"
      x="130"
      y="70"
      width="64"
      height="4"
      rx="2"
    />
    <text
      className="ga-text ga-muted ga-drag-count4"
      x="210"
      y="63"
      fontSize="9"
      textAnchor="end"
    >
      4 / 5
    </text>
    <text
      className="ga-text ga-drag-count5"
      x="210"
      y="63"
      fontSize="9"
      textAnchor="end"
    >
      5 / 5
    </text>
    <rect
      className="ga-stroke-primary ga-dropzone"
      x="130"
      y="84"
      width="80"
      height="76"
      rx="8"
      strokeDasharray="4 4"
    />
    <text
      className="ga-text ga-muted ga-drop-hint"
      x="170"
      y="126"
      fontSize="8"
      textAnchor="middle"
    >
      Drop samples here
    </text>
    <g className="ga-dragtile">
      <Tile x={32} y={76} size={48} />
    </g>
    <circle
      className="ga-fill-success ga-pop ga-seed-check"
      cx="170"
      cy="122"
      r="11"
    />
    <path
      className="ga-check-draw ga-seed-check-path"
      d="M164 122l4.5 4.5l8 -9"
    />
    <Cursor className="ga-cur-drag" />
  </Scene>
);

// Start button turns on, gets clicked, and propagation begins.
export const StartScene = () => (
  <Scene label="Clicking Start Label Propagation" className={styles.seedScene}>
    <rect
      className="ga-btn-disabled ga-start-off"
      x="30"
      y="78"
      width="180"
      height="36"
      rx="8"
    />
    <rect
      className="ga-fill-primary ga-start-on"
      x="30"
      y="78"
      width="180"
      height="36"
      rx="8"
    />
    <text className="ga-text" x="120" y="100" fontSize="11" textAnchor="middle">
      Start Label Propagation
    </text>
    <circle
      className="ga-stroke-primary ga-ripple ga-start-ripple"
      cx="120"
      cy="96"
      r="8"
      strokeWidth="2"
    />
    <circle
      className="ga-outline ga-start-ring-bg"
      cx="120"
      cy="150"
      r="11"
      strokeWidth="3"
    />
    <circle
      className="ga-stroke-primary ga-start-ring"
      cx="120"
      cy="150"
      r="11"
      strokeWidth="3"
      strokeLinecap="round"
      strokeDasharray="69"
      strokeDashoffset="69"
      transform="rotate(-90 120 150)"
    />
    <text
      className="ga-text ga-muted ga-start-label"
      x="120"
      y="178"
      fontSize="9"
      textAnchor="middle"
    >
      Initializing
    </text>
    <Cursor className="ga-cur-start" />
  </Scene>
);

const MARKER_TILES = [
  {
    x: 18,
    y: 36,
    defect: { dx: 44, dy: 40 },
    ok: true,
    caption: "Inside: label it",
  },
  {
    x: 128,
    y: 36,
    defect: { dx: 16, dy: 78 },
    ok: false,
    caption: "Outside: skip it",
  },
];

// The pink area of interest: only a defect inside the box counts for this image.
export const MarkerScene = () => (
  <Scene
    label="A defect inside the pink box counts, a defect outside does not"
    className={classNames(styles.seedScene, shared.shared)}
  >
    {MARKER_TILES.map((t, i) => (
      <g key={t.caption}>
        <Tile x={t.x} y={t.y} size={94} />
        <rect
          className="ga-marker-box"
          x={t.x + 30}
          y={t.y + 28}
          width="34"
          height="38"
        />
        <circle
          className="ga-in ga-defect"
          style={{ animationDelay: `${0.6 + i * 0.8}s` }}
          cx={t.x + t.defect.dx}
          cy={t.y + t.defect.dy}
          r="4"
        />
        <g className="ga-in" style={{ animationDelay: `${1.6 + i * 0.8}s` }}>
          <circle
            className={t.ok ? "ga-fill-success" : "ga-fill-error"}
            cx={t.x + 84}
            cy={t.y + 10}
            r="9"
          />
          <path
            className="ga-icon"
            d={
              t.ok
                ? `M${t.x + 79.5} ${t.y + 10}l3 3l6 -6`
                : `M${t.x + 80.5} ${t.y + 6.5}l7 7M${t.x + 87.5} ${t.y + 6.5}l-7 7`
            }
          />
        </g>
        <text
          className="ga-text ga-muted"
          x={t.x + 47}
          y={t.y + 112}
          fontSize="8"
          textAnchor="middle"
        >
          {t.caption}
        </text>
      </g>
    ))}
  </Scene>
);
